import { useCallback, useEffect, useState } from 'react';
import updateCart from '~/firebase/update/updateCart';

import { useRecoilState, useSetRecoilState } from 'recoil';
import ordersAtom from '~/recoil/atom/orders';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';

import { addHours, addMinutes, isSameDay, startOfDay } from 'date-fns';

import * as fp from 'lodash/fp';

import { PLATFORM_ID } from '~/globalVariables';

import platformAtom from '~/recoil/atom/platform';
import { Schedule } from '~/types/common';

function getDateMinutes(date: Date) {
  return date.getHours() * 60 + date.getMinutes();
}

function addHoursToCurrentTime(beforeHours: number) {
  if (typeof beforeHours !== 'number') {
    return new Date();
  }
  const now = new Date();
  return addHours(now, beforeHours);
}

export default function usePickMealTime() {
  const setOrders = useSetRecoilState(ordersAtom);
  const [pickMealAt, setPickMealAt] = useRecoilState(pickMealAtAtom);
  const [pickMealEnd, setPickMealEnd] = useRecoilState(pickMealEndAtom);
  const [pickMealDay, setPickMealDay] = useState({
    pickMealAt,
    pickMealEnd,
  });
  const [pickMealTimeMin, setPickMealTimeMin] = useState(
    fp.isDate(pickMealEnd) && fp.isDate(pickMealAt)
      ? {
          pickMealAtTimeMin: getDateMinutes(pickMealAt),
          pickMealEndTimeMin: getDateMinutes(pickMealEnd),
        }
      : null,
  );

  const [platform] = useRecoilState(platformAtom);
  const earliestPickMealDate = addHoursToCurrentTime(platform.preOrderSetting?.beforeHours);

  const handleResetTime = useCallback(() => {
    setPickMealDay({
      pickMealAt,
      pickMealEnd,
    });
    setPickMealTimeMin(
      fp.isDate(pickMealEnd) && fp.isDate(pickMealAt)
        ? {
            pickMealAtTimeMin: getDateMinutes(pickMealAt),
            pickMealEndTimeMin: getDateMinutes(pickMealEnd),
          }
        : null,
    );
  }, [pickMealAt, pickMealEnd]);

  useEffect(() => {
    handleResetTime();
  }, [handleResetTime]);

  const handleChangePickMealDayAt = (changeDate: Date) => {
    if (pickMealDay.pickMealAt && isSameDay(pickMealDay.pickMealAt, changeDate)) {
      return;
    }

    setPickMealDay({
      pickMealAt: changeDate,
      pickMealEnd: changeDate,
    });
    setPickMealTimeMin(null);
  };

  const handleChangePickMealTime = (schedule: Schedule | null) => {
    setPickMealTimeMin(
      schedule ? { pickMealAtTimeMin: schedule.start, pickMealEndTimeMin: schedule.end } : null,
    );
  };

  const handleUpdatePickMealTime = async (cartId: string): Promise<boolean> => {
    const { pickMealAt: userPickMealAt } = pickMealDay;
    if (!userPickMealAt || !pickMealTimeMin) {
      return false;
    }

    const combinedPickMealTime = {
      at: addMinutes(startOfDay(userPickMealAt), pickMealTimeMin.pickMealAtTimeMin),
      end: addMinutes(startOfDay(userPickMealAt), pickMealTimeMin.pickMealEndTimeMin),
    };

    if (
      fp.eq(pickMealAt?.getTime(), combinedPickMealTime.at.getTime()) &&
      fp.eq(pickMealEnd?.getTime(), combinedPickMealTime.end.getTime())
    ) {
      return true;
    }

    if (!cartId) {
      setPickMealAt(combinedPickMealTime.at);
      setPickMealEnd(combinedPickMealTime.end);
      setOrders([]);
      return true;
    }

    await updateCart(PLATFORM_ID, cartId, {
      pickMealEnd: combinedPickMealTime.end,
      pickMealAt: combinedPickMealTime.at,
      orders: [],
    });
    return true;
  };

  return {
    pickMealDay,
    pickMealTimeMin,
    earliestPickMealDate,
    todayOrderAfterHours: platform.preOrderSetting?.afterHours,
    handleChangePickMealDayAt,
    handleChangePickMealTime,
    handleUpdatePickMealTime,
    handleResetTime,
  };
}
